import { IconButton } from '@/shared/ui/primitives';
import type { FC, Ref } from 'react';
import { useId } from 'react';

import { sanitizeNumericInput } from '@/shared/lib/sanitizeNumericInput';
import { MinusIcon } from '../icons/MinusIcon';
import { PlusIcon } from '../icons/PlusIcon';
import styles from './NumberStepper.module.css';

interface NumberStepperProps {
  value: string;
  unlimited?: boolean;
  decrementDisabled?: boolean;
  invalid?: boolean;
  onInput: (value: string) => void;
  onStep: (delta: number) => void;
  onBlur?: () => void;
  inputRef?: Ref<HTMLInputElement>;
  inputTestId?: string;
  inputAriaLabel: string;
  decrementAriaLabel: string;
  incrementAriaLabel: string;
  decrementTestId?: string;
  incrementTestId?: string;
}

export const NumberStepper: FC<NumberStepperProps> = ({
  value,
  unlimited = false,
  decrementDisabled = false,
  invalid = false,
  onInput,
  onStep,
  onBlur,
  inputRef,
  inputTestId,
  inputAriaLabel,
  decrementAriaLabel,
  incrementAriaLabel,
  decrementTestId,
  incrementTestId,
}) => {
  const inputId = useId();

  return (
    <div className={styles.control} role="group" aria-label={`${inputAriaLabel} controls`}>
      {!unlimited && (
        <label
          className={styles.outerHitArea}
          htmlFor={inputId}
          aria-label={`Edit ${inputAriaLabel}`}
        />
      )}
      <IconButton
        type="button"
        variant="ghost"
        size="sm"
        className={styles.stepButton}
        onClick={() => onStep(-1)}
        disabled={unlimited || decrementDisabled}
        aria-label={decrementAriaLabel}
        data-testid={decrementTestId}
      >
        <MinusIcon />
      </IconButton>

      <label className={styles.valueArea}>
        {unlimited ? (
          <span className={styles.value} aria-hidden="true">
            ∞
          </span>
        ) : (
          <input
            id={inputId}
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            size={Math.max(1, Math.min(value.length, 8))}
            className={styles.value}
            value={value}
            onChange={(e) => onInput(sanitizeNumericInput(e.target.value))}
            onBlur={onBlur}
            placeholder="0"
            aria-label={inputAriaLabel}
            aria-invalid={invalid ? 'true' : 'false'}
            data-testid={inputTestId}
          />
        )}
      </label>

      <IconButton
        type="button"
        variant="ghost"
        size="sm"
        className={styles.stepButton}
        onClick={() => onStep(1)}
        disabled={unlimited}
        aria-label={incrementAriaLabel}
        data-testid={incrementTestId}
      >
        <PlusIcon />
      </IconButton>
      {!unlimited && (
        <label
          className={styles.outerHitArea}
          htmlFor={inputId}
          aria-label={`Edit ${inputAriaLabel}`}
        />
      )}
    </div>
  );
};
